import * as React from 'react';

import { PageProps } from "./page.types";
import { FieldSelectorOnChangeMultiple } from '@common/components/FieldSelector/field-selector.types';
import { SkipLogicCondition, SkipLogicConditionOperator } from '@common/components/skip-logic/skip-logic.types';
import { QuestionnaireSkipLogicConditionCreateDto } from '@common/dto/questionnaire/skip-logic/condition-create-dto';
import { generateId } from '@common/utils/common';
import { getUniqueFieldCombination } from '@common/utils/fields';
import { QuestionnairePageCreateLocalDto } from '@modules/fhq-design-tool/dto/page-create-local-dto';
import { QuestionnaireSkipLogicConditionListDto } from '@common/dto/questionnaire/skip-logic/condition-list-dto';
import { I18n } from 'react-redux-i18n';
import { FieldType } from '@common/dto/field/type';
import { QuestionnairePageUpdateDto } from '@common/dto/questionnaire/page/update-dto';
import { QuestionnaireFieldAddDto } from '@common/dto/questionnaire/field/add-dto';
import { QuestionnaireFieldUpdateDto } from '@common/dto/questionnaire/field/update-dto';
import { QuestionnaireFieldListDto } from '@common/dto/questionnaire/field/list-dto';
import { useTextBlock } from '../text-block/text-block.hook';
import { QuestionnaireTextBlockLocalDto } from '@modules/fhq-design-tool/dto/text-block-create-dto';
import { useFieldsWithTextBlock } from '../../../fields-with-text-blocks/fields-with-text-blocks.hooks';
import { UpdatedLocalFields, UpdatedLocalTexts } from '../../../fields-with-text-blocks/fields-with-text-blocks.types';

export const usePageController = (props: PageProps) => {
  const { cachedData, data, onChange, getPage, showErrorMessage, getFhq4QuestionnaireField, errors } = props;
  const [loadingPage, setLoadingPage] = React.useState(false);
  const [fieldChooserOpened, setFieldChooserOpened] = React.useState(false);
  const [editingFieldUniqueCombination, setEditingFieldUniqueCombination] = React.useState<string | undefined>();
  const [familyPagesSkipLogicConditions, setFamilyPagesSkipLogicConditions] = React.useState<SkipLogicCondition[]>([]);

  const initializeData = async () => {
    setLoadingPage(true);

    let skipLogicConditions: QuestionnaireSkipLogicConditionListDto[] = [];

    if (cachedData) {
      skipLogicConditions = cachedData.skipLogic || [];
    } else {
      try {
        const page = await getPage(data.id);
  
        skipLogicConditions = page.skipLogic || [];
      } catch (err) {
        console.error(err);
        showErrorMessage(I18n.t('Failed to get page'))
      }
    }
    
    setFamilyPagesSkipLogicConditions(skipLogicConditions.map((x) => ({ ...x, id: generateId() } as SkipLogicCondition)) || []);
    setLoadingPage(false)
  };

  React.useEffect(() => {
    initializeData();
  }, [data.id]);

  const updatePage = React.useCallback((dataToUpdate: Partial<QuestionnairePageUpdateDto | QuestionnairePageCreateLocalDto>) => {
    if (cachedData) {
      onChange({
        ...cachedData,
        ...dataToUpdate,
        modified: true,
      } as QuestionnairePageUpdateDto | QuestionnairePageCreateLocalDto);
    }
  }, [cachedData, onChange]);

  const {
    editingTextBlock,
    addTextBlock,
    handleOrderChange,
    handleTextBlockDelete,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
  } = useTextBlock({
    data: cachedData as { texts?: Array<QuestionnaireTextBlockLocalDto> },
    onChange: updatePage,
  });

  const fields = cachedData?.fields || [];
  const editingField = fields.find((x) => getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) === editingFieldUniqueCombination);

  const processedFields = React.useMemo(() => {
    return fields
      .map((x): FieldInList => ({
        id: getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }),
        name: x.name,
        original: x,
      }))
      .sort((a, b) => a.original.order - b.original.order);
  }, [fields]);

  const { getCombinedItems } = useFieldsWithTextBlock<FieldInList, QuestionnaireTextBlockLocalDto>({ fields: processedFields, texts: (cachedData?.texts || []) as Array<QuestionnaireTextBlockLocalDto> });
  const combinedItems = React.useMemo(getCombinedItems, [processedFields, cachedData?.texts]);

  const handlePageNameChange = React.useCallback((val: string) => {
    updatePage({
      title: val.trim()
    });
  }, [updatePage]);

  const handlePageDescriptionChange = React.useCallback((val: string) => {
    updatePage({
      description: val
    });
  }, [updatePage]);

  const handleUseSkipLogicChange = React.useCallback((val: string) => {
    updatePage({
      skipLogicEnabled: JSON.parse(val)
    });
  }, [updatePage]);

  const handleSkipLogicConditionAdd = React.useCallback(({ conditions }: { conditions: QuestionnaireSkipLogicConditionCreateDto[] }) => {
    const familyPagesSkipLogic: Array<SkipLogicCondition> = [
      ...familyPagesSkipLogicConditions,
      ...conditions.map((condition) => ({ ...condition, id: generateId(), operator: SkipLogicConditionOperator.equals } as SkipLogicCondition))
    ];

    updatePage({
      skipLogic: familyPagesSkipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionListDto)
    });
    setFamilyPagesSkipLogicConditions(familyPagesSkipLogic);
  }, [updatePage, familyPagesSkipLogicConditions]);

  const handleSkipLogicChange = React.useCallback((familyPagesSkipLogic: SkipLogicCondition[]) => {
    updatePage({
      skipLogic: familyPagesSkipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionCreateDto)
    });
    setFamilyPagesSkipLogicConditions(familyPagesSkipLogic);
  }, [updatePage]);

  const openFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(true);
  }, []);

  const closeFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(false);
  }, []);

  const handleFieldAdd = React.useCallback(
    async ({ fields }: FieldSelectorOnChangeMultiple) => {
      const newFields: Array<QuestionnaireFieldAddDto | QuestionnaireFieldListDto> = [];
      const existingFieldsLength = cachedData?.fields?.length || 0;

      await Promise.all(
        fields.map(async (field, fieldIndex) => {
          const hasSameField = cachedData?.fields.some(
            (x) =>
              getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) ===
              getUniqueFieldCombination({ id: field.fieldId, context: field.fieldContext, type: field.fieldTypeId })
          );

          if (hasSameField || !cachedData) {
            return;
          }

          try {
            const fullField = await getFhq4QuestionnaireField({
              id: field.fieldId,
              context: field.fieldContext,
              system: field.systemField,
            });
            
            const newField: QuestionnaireFieldAddDto | QuestionnaireFieldListDto = {
              progenyId: field.fieldId as number,
              name: field.fieldName,
              path: fullField.path || fullField.name,
              order: existingFieldsLength + fieldIndex + 1,
              systemField: field.systemField,
              context: field.fieldContext,
              type: fullField.type,
              progenyFieldType: field.fieldTypeId as FieldType,
              description: fullField.description,
              required: fullField.required,
              readonly: fullField.required,
              defaultValue: fullField.defaultValue,
              allowDuplications: fullField.allowDuplications,
              skipLogic: fullField.skipLogic,
              options: fullField.options,
              subfields: fullField.subfields,
              skipLogicEnabled: fullField.skipLogicEnabled,
              displayInFHQ: fullField.displayInFHQ,
              possibleDropdownOptions: fullField.possibleDropdownOptions,
            };

            newFields.push(newField);
          } catch (err) {
            console.error(err);
            showErrorMessage(`${I18n.t("Failed to get field with fieldName", { fieldName: field.fieldName })}`);
          }
        })
      );

      if (!newFields.length) {
        return;
      }

      updatePage({
        fields: [...(cachedData?.fields || []), ...newFields],
      });
    },
    [cachedData, updatePage, showErrorMessage, getFhq4QuestionnaireField]
  );

  type FieldInList = {
    id: string;
    name: string;
    original: QuestionnaireFieldUpdateDto | QuestionnaireFieldAddDto;
  };

  const handleFieldsOrderChange = React.useCallback((newFieldsOrder: Array<FieldInList>) => {
    updatePage({
      fields: newFieldsOrder.map((x, i) => ({
        ...x.original,
        order: i + 1,
      }))
    });
  }, [updatePage]);

  const handleFieldDelete = React.useCallback((field: FieldInList) => {
    updatePage({
      fields: cachedData?.fields
        ?.filter((x) => 
          getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) !== getUniqueFieldCombination({ id: field.original.progenyId, context: field.original.context, type: field.original.progenyFieldType })
        )
        .sort((a, b) => a.order - b.order)
        .map((f, i) => ({
          ...f,
          order: i + 1,
        }))
    });
  }, [cachedData, updatePage]);

  const handleFieldChange = React.useCallback((field: QuestionnaireFieldUpdateDto | QuestionnaireFieldAddDto) => {
    updatePage({
      fields: cachedData?.fields?.map((x) => 
        getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) === getUniqueFieldCombination({ id: field.progenyId, context: field.context, type: field.progenyFieldType }) ? field : x
      ),
    });
  }, [cachedData, updatePage]);

  const navigateOnFieldEdit = React.useCallback((field: FieldInList) => {
    setEditingFieldUniqueCombination(getUniqueFieldCombination({ id: field.original.progenyId, context: field.original.context, type: field.original.progenyFieldType }));
  }, []);

  const goBackFromFieldEdit = React.useCallback(() => {
    setEditingFieldUniqueCombination(undefined);
  }, []);

  const canShowHideField = React.useCallback(() => {
    return false;
  }, []);

  const fieldHasError = React.useCallback((field: FieldInList) => {
    return errors?.fields ? Boolean(errors?.fields[field.original.progenyId]) : false;
  }, [errors]);

  const handleAddTextBlock = React.useCallback(() => {
    addTextBlock({ startOrder: combinedItems.length });
  }, [addTextBlock, combinedItems]);

  const handleFieldsOrTextBlocksChange = React.useCallback((params: {
    texts: UpdatedLocalTexts<QuestionnaireTextBlockLocalDto>;
    fields: UpdatedLocalFields<FieldInList>;
}) => {
    updatePage({
      texts: params.texts,
      fields: params.fields.map(({order, ...field}) => ({ ...field.original, order }))
    })
  }, [updatePage]);

  return {
    editingTextBlock,
    addTextBlock,
    handleOrderChange,
    handleTextBlockDelete,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
    fieldHasError,
    loadingPage,
    processedFields,
    familyPagesSkipLogicConditions,
    handleUseSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleSkipLogicChange,
    handleFieldChange,
    editingField,
    goBackFromFieldEdit,
    openFieldChooser,
    closeFieldChooser,
    fieldChooserOpened,
    handleFieldAdd,
    handlePageNameChange,
    handlePageDescriptionChange,
    handleFieldsOrderChange,
    handleFieldDelete,
    navigateOnFieldEdit,
    canShowHideField,
    handleFieldsOrTextBlocksChange,
    handleAddTextBlock,
    combinedItems
  };
};